import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowBack,
  LocalOffer,
  Person,
  Notes,
  MoneyOff,
  QuestionAnswer,
  Info,
} from '@mui/icons-material';

import Loading from '../../../components/global/loading/Loading';
import { useInquiry } from '../../../context/InquiryContext';
import OfferForm from '../../../forms/OfferForm';
import { Inquiry } from '../../../models/Inquiry';

function AddOffer() {
  const navigate = useNavigate();
  const { inquiryId } = useParams();
  const { getInquiry } = useInquiry();
  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchInquiry() {
      setIsLoading(true);
      try {
        if (inquiryId) {
          const inquiryData = await getInquiry(parseInt(inquiryId));
          setInquiry(inquiryData);
        } else {
          throw new Error('ID zapytania nie zostało zdefiniowane');
        }
      } catch (error) {
        console.error('Błąd pobierania zapytania:', error);
        navigate('/admin/zapytania');
      } finally {
        setIsLoading(false);
      }
    }

    if (!inquiryId) {
      navigate('/admin/zapytania');
    } else {
      fetchInquiry();
    }
  }, [getInquiry, inquiryId, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mt-8">
        <p className="font-medium">
          Nie znaleziono zapytania o ID: {inquiryId}
        </p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="bg-white border border-red-500 text-red-600 px-4 py-2 rounded-md mt-3 text-sm font-medium flex items-center"
          onClick={() => navigate('/admin/zapytania')}
        >
          <ArrowBack fontSize="small" className="mr-1" /> Wróć do listy zapytań
        </motion.button>
      </div>
    );
  }

  // Formatowanie daty
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6">
      {/* Przycisk powrotu */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-4"
      >
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          onClick={() => navigate('/admin/oferty')}
        >
          <ArrowBack fontSize="small" />
          <span className="font-medium">Powrót do listy ofert</span>
        </motion.button>
      </motion.div>

      {/* Nagłówek formularza */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="mb-6"
      >
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <LocalOffer className="text-primary mr-2" />
          Utwórz ofertę dla zapytania #{inquiry.id}
        </h1>
        <p className="text-gray-600 mt-1">
          Wypełnij formularz, aby utworzyć spersonalizowaną ofertę dla klienta.
        </p>
      </motion.div>

      {/* Sekcja informacyjna o zapytaniu */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="mb-6"
      >
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <QuestionAnswer className="text-primary" />
            <h2 className="text-lg font-medium text-gray-900">
              Szczegóły zapytania klienta
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Informacje o kliencie */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Person fontSize="small" className="mr-1 text-primary" />
                Dane klienta
              </h3>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Nazwa/Imię i nazwisko</p>
                  <p className="font-medium">{inquiry.name}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium">{inquiry.email}</p>
                </div>

                {inquiry.nip && (
                  <div>
                    <p className="text-xs text-gray-500">NIP</p>
                    <p className="font-medium">{inquiry.nip}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs text-gray-500">Data zapytania</p>
                  <p className="font-medium">{formatDate(inquiry.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Uwagi klienta */}
            <div
              className={`bg-gray-50 rounded-lg p-4 ${inquiry.remarks ? 'md:col-span-2' : ''}`}
            >
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Notes fontSize="small" className="mr-1 text-primary" />
                Uwagi klienta
              </h3>

              {inquiry.remarks ? (
                <div className="bg-white p-3 rounded-md border border-gray-100 text-gray-700 italic">
                  "{inquiry.remarks}"
                </div>
              ) : (
                <p className="text-gray-500">
                  Klient nie dodał dodatkowych uwag
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Sekcja produktów */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="mb-8"
      >
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <Info className="text-primary" />
            <h2 className="text-lg font-medium text-gray-900">
              Produkty w zapytaniu
            </h2>
          </div>

          {inquiry.products && inquiry.products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {inquiry.products.map((item) => (
                <div
                  key={item.productId}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-100 flex flex-col"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="h-20 w-20 bg-white border border-gray-200 rounded-md overflow-hidden flex-shrink-0">
                      {item.product?.mobilePrimaryImage ? (
                        <img
                          src={item.product.mobilePrimaryImage}
                          alt={item.product.name}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-gray-100">
                          <MoneyOff className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">
                        {item.product?.name || 'Produkt niedostępny'}
                      </h4>
                      <p className="text-xs text-gray-500 mb-2">
                        SKU: {item.product?.serialNumber || 'N/A'}
                      </p>
                      {item.product?.category && (
                        <span className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-md">
                          {item.product.category.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-md border border-gray-100 mt-auto">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-sm">
                        <span className="text-gray-500">Ilość:</span>
                        <span className="font-medium ml-1 text-gray-900">
                          {item.quantity} szt.
                        </span>
                      </div>

                      <div className="text-sm">
                        <span className="text-gray-500">Cena katalog.:</span>
                        <span className="font-medium ml-1 text-gray-900">
                          {item.product?.price?.toFixed(2) || '0.00'} zł
                        </span>
                      </div>
                    </div>

                    {item.product?.price && (
                      <div className="text-right text-xs text-gray-500">
                        Wartość:{' '}
                        <span className="font-medium text-primary">
                          {(item.product.price * item.quantity).toFixed(2)} zł
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 p-8 rounded-md text-center">
              <p className="text-gray-500 italic">Brak produktów w zapytaniu</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Formularz oferty */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        {inquiry && <OfferForm inquiry={inquiry} />}
      </motion.div>
    </div>
  );
}

export default AddOffer;
